import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../services/api';
import AdminSidebar from '../components/AdminSidebar';
import DashboardHeader from '../components/DashboardHeader';
import { getStoredGeminiModel } from '../utils/geminiModel';
import './Dashboard.css';

const LINE_TEXTAREA_STYLE = {
  fontFamily: 'ui-monospace, monospace',
  fontSize: 14,
  lineHeight: 1.5,
};

const textInputStyle = {
  ...LINE_TEXTAREA_STYLE,
  width: '100%',
  boxSizing: 'border-box',
};

function WritingSkillExample() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveNextLoading, setSaveNextLoading] = useState(false);
  const [nextLoading, setNextLoading] = useState(false);
  const [autoFindLoading, setAutoFindLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [autoFindCount, setAutoFindCount] = useState('5');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [exampleSearchQuery, setExampleSearchQuery] = useState('');
  const [exampleSearchResults, setExampleSearchResults] = useState([]);
  const [exampleSearchLoading, setExampleSearchLoading] = useState(false);
  const [exampleSearchError, setExampleSearchError] = useState('');
  const [exampleSearchFocused, setExampleSearchFocused] = useState(false);
  const [example, setExample] = useState('');
  const [selectedExampleId, setSelectedExampleId] = useState(null);
  const [selectedSkillIds, setSelectedSkillIds] = useState(() => new Set());
  const [generatedExamples, setGeneratedExamples] = useState([]);
  const exampleSearchDebounceRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const loadSkills = async () => {
      setLoading(true);
      try {
        const res = await api.get('/data/writing-skill/examples/skills');
        if (!cancelled) setGroups(res.data?.groups || []);
      } catch (err) {
        if (!cancelled) {
          setMessage({
            type: 'error',
            text: err.response?.data?.error || err.response?.data?.details || 'Failed to load skills',
          });
          setGroups([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadSkills();
    return () => {
      cancelled = true;
    };
  }, []);

  const refreshSkillGroups = useCallback(async () => {
    const res = await api.get('/data/writing-skill/examples/skills');
    setGroups(res.data?.groups || []);
  }, []);

  const fetchExamples = useCallback(async (q) => {
    const trimmed = String(q).trim();

    setExampleSearchLoading(true);
    setExampleSearchError('');
    try {
      const params = trimmed ? { q: trimmed } : {};
      const res = await api.get('/data/writing-skill/examples', { params });
      setExampleSearchResults(res.data?.examples || []);
    } catch (err) {
      setExampleSearchResults([]);
      setExampleSearchError(
        err.response?.data?.error || err.response?.data?.details || 'Failed to search examples'
      );
    } finally {
      setExampleSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    if (exampleSearchDebounceRef.current) clearTimeout(exampleSearchDebounceRef.current);
    const trimmed = exampleSearchQuery.trim();
    if (!exampleSearchFocused) {
      return () => {};
    }

    exampleSearchDebounceRef.current = setTimeout(() => {
      fetchExamples(trimmed);
    }, 350);
    return () => {
      if (exampleSearchDebounceRef.current) clearTimeout(exampleSearchDebounceRef.current);
    };
  }, [exampleSearchFocused, exampleSearchQuery, fetchExamples]);

  const toggleSkill = (skillId) => {
    setSelectedSkillIds((prev) => {
      const next = new Set(prev);
      if (next.has(skillId)) next.delete(skillId);
      else next.add(skillId);
      return next;
    });
  };

  const skillNameById = new Map(
    groups.flatMap((group) => group.skills.map((skill) => [skill.skill_id, skill.name || `(id ${skill.skill_id})`]))
  );

  const getSkillLabel = (skillId) => skillNameById.get(skillId) || `(id ${skillId})`;
  const skillOverviewRows = groups.flatMap((group) =>
    group.skills.map((skill) => ({
      skillId: skill.skill_id,
      name: skill.name || `(id ${skill.skill_id})`,
      exampleCount: Number.isFinite(Number(skill.exampleCount)) ? Number(skill.exampleCount) : 0,
    }))
  ).sort((a, b) => b.exampleCount - a.exampleCount || a.name.localeCompare(b.name));

  const pickExample = (item) => {
    setExample(item.value || '');
    setSelectedExampleId(item.skill_example_id ?? null);
    setSelectedSkillIds(new Set(Array.isArray(item.skillIds) ? item.skillIds : []));
    setGeneratedExamples([]);
    setExampleSearchQuery('');
    setExampleSearchResults([]);
    setExampleSearchError('');
  };

  const handleAutoFind = async () => {
    const count = parseInt(autoFindCount, 10);
    if (Number.isNaN(count) || count < 1 || count > 50) {
      setMessage({ type: 'error', text: 'How many must be an integer from 1 to 50.' });
      return;
    }
    if (selectedSkillIds.size === 0) {
      setMessage({ type: 'error', text: 'Select at least one skill before Auto Generate.' });
      return;
    }

    setAutoFindLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await api.post('/data/writing-skill/examples/auto-find', {
        count,
        skillIds: [...selectedSkillIds],
        model: getStoredGeminiModel(),
      });
      const examples = (Array.isArray(res.data?.examples) ? res.data.examples : []).map((item) => ({
        value: item?.value != null ? String(item.value) : '',
        skillIds: Array.isArray(item?.skillIds) ? item.skillIds : [...selectedSkillIds],
      }));
      setGeneratedExamples(examples);
      setExample('');
      setSelectedExampleId(null);
      setExampleSearchFocused(false);
      setExampleSearchQuery('');
      setExampleSearchResults([]);
      setExampleSearchError('');
      setMessage({
        type: examples.length > 0 ? 'success' : 'error',
        text:
          examples.length > 0
            ? `Generated ${examples.length} example(s). Review them, then Save to insert.`
            : 'No new non-duplicate examples found.',
      });
    } catch (err) {
      setMessage({
        type: 'error',
        text:
          err.response?.data?.error ||
          err.response?.data?.details ||
          'Failed to auto find examples',
      });
    } finally {
      setAutoFindLoading(false);
    }
  };

  const updateGeneratedExample = (index, value) => {
    setGeneratedExamples((prev) =>
      prev.map((item, i) => (i === index ? { ...item, value } : item))
    );
  };

  const deleteGeneratedExample = (index) => {
    setGeneratedExamples((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDelete = async () => {
    if (selectedExampleId == null) {
      setMessage({ type: 'error', text: 'Select an existing example to delete.' });
      return;
    }

    setDeleteLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await api.delete(`/data/writing-skill/examples/${selectedExampleId}`);
      const nextExample = res.data?.nextExample || null;
      setExampleSearchResults((prev) =>
        prev.filter((item) => item.skill_example_id !== selectedExampleId)
      );
      if (nextExample) {
        setExample(nextExample.value || '');
        setSelectedExampleId(nextExample.skill_example_id ?? null);
        setSelectedSkillIds(new Set(Array.isArray(nextExample.skillIds) ? nextExample.skillIds : []));
        setMessage({ type: 'success', text: 'Deleted. Loaded next example.' });
      } else {
        setExample('');
        setSelectedExampleId(null);
        setSelectedSkillIds(new Set());
        setMessage({ type: 'success', text: 'Deleted. No next example found.' });
      }
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.error || err.response?.data?.details || 'Failed to delete',
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const saveGeneratedExamples = async () => {
    const examples = generatedExamples.map((item) => ({
      value: String(item.value || '').trim(),
      skillIds: Array.isArray(item.skillIds) ? item.skillIds : [],
    }));

    for (let i = 0; i < examples.length; i++) {
      if (!examples[i].value) {
        setMessage({ type: 'error', text: `Generated example ${i + 1} is empty.` });
        return null;
      }
      if (examples[i].skillIds.length === 0) {
        setMessage({ type: 'error', text: `Generated example ${i + 1} has no skills.` });
        return null;
      }
    }

    const res = await api.post('/data/writing-skill/examples/save-many', { examples });
    setGeneratedExamples([]);
    setExample('');
    setSelectedExampleId(null);
    setSelectedSkillIds(new Set());
    setExampleSearchQuery('');
    setExampleSearchResults([]);
    await refreshSkillGroups();
    return res.data;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (generatedExamples.length > 0) {
      setSaveLoading(true);
      setMessage({ type: '', text: '' });
      try {
        const data = await saveGeneratedExamples();
        if (!data) return;
        setMessage({
          type: 'success',
          text: `Saved ${data.insertedCount ?? generatedExamples.length} generated example(s).`,
        });
      } catch (err) {
        setMessage({
          type: 'error',
          text: err.response?.data?.error || err.response?.data?.details || 'Failed to save generated examples',
        });
      } finally {
        setSaveLoading(false);
      }
      return;
    }

    const value = example.trim();
    if (!value) {
      setMessage({ type: 'error', text: 'Example is required.' });
      return;
    }
    if (selectedSkillIds.size === 0) {
      setMessage({ type: 'error', text: 'Select at least one skill.' });
      return;
    }

    setSaveLoading(true);
    setMessage({ type: '', text: '' });
    try {
      await api.post('/data/writing-skill/examples/save', {
        skillExampleId: selectedExampleId,
        value,
        skillIds: [...selectedSkillIds],
      });
      setMessage({ type: 'success', text: 'Saved.' });
      setExample('');
      setSelectedExampleId(null);
      setSelectedSkillIds(new Set());
      setExampleSearchQuery('');
      setExampleSearchResults([]);
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.error || err.response?.data?.details || 'Failed to save',
      });
    } finally {
      setSaveLoading(false);
    }
  };

  const handleSaveAndNext = async () => {
    if (generatedExamples.length > 0) {
      const currentExampleId = selectedExampleId;
      setSaveNextLoading(true);
      setMessage({ type: '', text: '' });
      try {
        const data = await saveGeneratedExamples();
        if (!data) return;

        if (currentExampleId != null) {
          const nextRes = await api.get(`/data/writing-skill/examples/${currentExampleId}/next`);
          const nextExample = nextRes.data?.nextExample || null;
          if (nextExample) {
            setExample(nextExample.value || '');
            setSelectedExampleId(nextExample.skill_example_id ?? null);
            setSelectedSkillIds(new Set(Array.isArray(nextExample.skillIds) ? nextExample.skillIds : []));
            setMessage({
              type: 'success',
              text: `Saved ${data.insertedCount ?? generatedExamples.length} generated example(s). Loaded next example.`,
            });
          } else {
            setMessage({
              type: 'success',
              text: `Saved ${data.insertedCount ?? generatedExamples.length} generated example(s). No next example found.`,
            });
          }
        } else {
          setMessage({
            type: 'success',
            text: `Saved ${data.insertedCount ?? generatedExamples.length} generated example(s).`,
          });
        }
      } catch (err) {
        setMessage({
          type: 'error',
          text:
            err.response?.data?.error ||
            err.response?.data?.details ||
            'Failed to save generated examples',
        });
      } finally {
        setSaveNextLoading(false);
      }
      return;
    }

    if (selectedExampleId == null) {
      setMessage({ type: 'error', text: 'Select an existing example first.' });
      return;
    }

    const value = example.trim();
    if (!value) {
      setMessage({ type: 'error', text: 'Example is required.' });
      return;
    }
    if (selectedSkillIds.size === 0) {
      setMessage({ type: 'error', text: 'Select at least one skill.' });
      return;
    }

    setSaveNextLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await api.post(`/data/writing-skill/examples/${selectedExampleId}/save-and-next`, {
        value,
        skillIds: [...selectedSkillIds],
      });
      const nextExample = res.data?.nextExample || null;
      if (nextExample) {
        setExample(nextExample.value || '');
        setSelectedExampleId(nextExample.skill_example_id ?? null);
        setSelectedSkillIds(new Set(Array.isArray(nextExample.skillIds) ? nextExample.skillIds : []));
        setExampleSearchQuery('');
        setExampleSearchResults([]);
        setMessage({ type: 'success', text: 'Saved. Loaded next example.' });
      } else {
        setMessage({ type: 'success', text: 'Saved. No next example found.' });
      }
    } catch (err) {
      setMessage({
        type: 'error',
        text:
          err.response?.data?.error ||
          err.response?.data?.details ||
          'Failed to save and load next',
      });
    } finally {
      setSaveNextLoading(false);
    }
  };

  const handleNext = async () => {
    if (selectedExampleId == null) {
      setMessage({ type: 'error', text: 'Select an existing example first.' });
      return;
    }

    setNextLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await api.get(`/data/writing-skill/examples/${selectedExampleId}/next`);
      const nextExample = res.data?.nextExample || null;
      if (nextExample) {
        setExample(nextExample.value || '');
        setSelectedExampleId(nextExample.skill_example_id ?? null);
        setSelectedSkillIds(new Set(Array.isArray(nextExample.skillIds) ? nextExample.skillIds : []));
        setExampleSearchQuery('');
        setExampleSearchResults([]);
        setMessage({ type: 'success', text: 'Loaded next example.' });
      } else {
        setMessage({ type: 'error', text: 'No next example found.' });
      }
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.error || err.response?.data?.details || 'Failed to load next',
      });
    } finally {
      setNextLoading(false);
    }
  };

  return (
    <div className="dashboard">
      <DashboardHeader title="Writing Skill Example" />

      <div className="dashboard-layout">
        <AdminSidebar />
        <div className="dashboard-content" style={{ maxWidth: 900, margin: '0 auto' }}>
          {message.text && <div className={`message ${message.type}`}>{message.text}</div>}

          <div className="form-section">
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 16,
              }}
            >
              <h2>Writing Skill Example</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap'}}>
                <button
                  type="button"
                  className="delete-question-button"
                  onClick={handleDelete}
                  disabled={
                    selectedExampleId == null ||
                    saveLoading ||
                    saveNextLoading ||
                    nextLoading ||
                    autoFindLoading ||
                    deleteLoading
                  }
                >
                  {deleteLoading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>

            <form onSubmit={handleSave}>
              <div className="form-group">
                <label htmlFor="wse-auto-find-count">Numbers</label>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    id="wse-auto-find-count"
                    type="number"
                    min={1}
                    max={50}
                    step={1}
                    value={autoFindCount}
                    onChange={(e) => setAutoFindCount(e.target.value)}
                    disabled={saveLoading || saveNextLoading || nextLoading || autoFindLoading || deleteLoading}
                    style={{ ...textInputStyle, maxWidth: 120 }}
                  />
                  <button
                    type="button"
                    className="submit-button"
                    onClick={handleAutoFind}
                    disabled={saveLoading || saveNextLoading || nextLoading || autoFindLoading || deleteLoading}
                  >
                    {autoFindLoading ? 'Generating...' : 'Auto Generate'}
                  </button>
                </div>
              </div>

              {generatedExamples.length > 0 ? (
                <div className="form-group">
                  <h3 style={{ margin: '0 0 12px' }}>Generated examples</h3>
                  <p className="form-hint" style={{ marginTop: 0 }}>
                    These sentences are not in the database yet. Edit them here, then use Save or Save and Next.
                  </p>
                  <div className="generated-questions-list">
                    {generatedExamples.map((item, index) => (
                      <div key={index} className="generated-question-item">
                        <div className="question-item-header">
                          <h4 className="question-item-title">Example {index + 1}</h4>
                          <button
                            type="button"
                            className="delete-question-button"
                            onClick={() => deleteGeneratedExample(index)}
                            disabled={saveLoading || saveNextLoading || nextLoading || autoFindLoading || deleteLoading}
                          >
                            Delete
                          </button>
                        </div>
                        <div className="form-group">
                          <label>
                            Sentence <span className="required">*</span>
                          </label>
                          <textarea
                            value={item.value}
                            onChange={(e) => updateGeneratedExample(index, e.target.value)}
                            disabled={saveLoading || saveNextLoading || nextLoading || autoFindLoading || deleteLoading}
                            rows={3}
                            style={{
                              ...textInputStyle,
                              minHeight: 90,
                              resize: 'vertical',
                            }}
                          />
                        </div>
                        <small className="form-hint">
                          Skills: {(Array.isArray(item.skillIds) ? item.skillIds : []).map(getSkillLabel).join(', ')}
                        </small>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="form-group">
                <label htmlFor="wse-example-search">Find existing example</label>
                <input
                  id="wse-example-search"
                  type="text"
                  value={exampleSearchQuery}
                  onChange={(e) => setExampleSearchQuery(e.target.value)}
                  onFocus={() => setExampleSearchFocused(true)}
                  onBlur={() => setExampleSearchFocused(false)}
                  placeholder="Search or pick an example..."
                  autoComplete="off"
                  disabled={saveLoading || saveNextLoading || nextLoading || autoFindLoading || deleteLoading}
                />
                {exampleSearchLoading ? <small>Searching...</small> : null}
                {exampleSearchError ? <div className="message error">{exampleSearchError}</div> : null}
                {exampleSearchFocused && exampleSearchResults.length > 0 ? (
                  <ul className="sidebar-submenu" style={{ marginTop: 8, maxHeight: 220, overflow: 'auto' }}>
                    {exampleSearchResults.map((item) => (
                      <li
                        key={item.skill_example_id}
                        className="sidebar-item"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          pickExample(item);
                          setExampleSearchFocused(false);
                        }}
                      >
                        {item.skill_example_id}: {item.value || '(empty)'}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>

              <div className="form-group">
                <label htmlFor="wse-example">
                  Example <span className="required">*</span>
                </label>
                <textarea
                  id="wse-example"
                  value={example}
                  onChange={(e) => setExample(e.target.value)}
                  disabled={saveLoading || saveNextLoading || nextLoading || autoFindLoading || deleteLoading}
                  rows={3}
                  style={{
                    ...textInputStyle,
                    minHeight: 90,
                    resize: 'vertical',
                  }}
                />
              </div>

              <div className="form-group">
                <span style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>
                  Skills <span className="required">*</span>
                </span>
                {loading ? (
                  <div className="loading-state">Loading skills...</div>
                ) : (
                  <div style={{ display: 'grid', gap: 20, minWidth: 0 }}>
                    {groups.map((group) => (
                      <section key={group.skill_type_id} style={{ minWidth: 0 }}>
                        <h3 style={{ margin: '0 0 12px' }}>
                          {group.value || `(type id ${group.skill_type_id})`}
                        </h3>
                        {group.skills.length === 0 ? (
                          <div className="empty-state">No skills</div>
                        ) : (
                          <div
                            style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: '10px 16px',
                              alignItems: 'center',
                              width: '100%',
                              maxWidth: '100%',
                            }}
                          >
                            {group.skills.map((skill) => (
                              <label
                                key={skill.skill_id}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 6,
                                  cursor: 'pointer',
                                  flex: '0 0 auto',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedSkillIds.has(skill.skill_id)}
                                  onChange={() => toggleSkill(skill.skill_id)}
                                  disabled={saveLoading || saveNextLoading || nextLoading || autoFindLoading || deleteLoading}
                                />
                                <span>{skill.name || `(id ${skill.skill_id})`}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </section>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ display: 'grid', gap: 12, marginTop: 20 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <button
                    type="submit"
                    className="submit-button"
                    disabled={saveLoading || saveNextLoading || nextLoading || autoFindLoading || deleteLoading || loading}
                  >
                    {saveLoading ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    type="button"
                    className="submit-button"
                    onClick={handleNext}
                    disabled={
                      selectedExampleId == null ||
                      saveLoading ||
                      saveNextLoading ||
                      nextLoading ||
                      autoFindLoading ||
                      deleteLoading ||
                      loading
                    }
                  >
                    {nextLoading ? 'Loading...' : 'Next'}
                  </button>
                </div>
                <button
                  type="button"
                  className="submit-button"
                  onClick={handleSaveAndNext}
                  style={{ width: '100%' }}
                  disabled={
                    (selectedExampleId == null && generatedExamples.length === 0) ||
                    saveLoading ||
                    saveNextLoading ||
                    nextLoading ||
                    autoFindLoading ||
                    deleteLoading ||
                    loading
                  }
                >
                  {saveNextLoading ? 'Saving...' : 'Save and Next'}
                </button>
              </div>

              <div className="form-group" style={{ marginTop: 24 }}>
                <h3 style={{ margin: '0 0 12px' }}>Overview</h3>
                {loading ? (
                  <div className="loading-state">Loading overview...</div>
                ) : skillOverviewRows.length === 0 ? (
                  <div className="empty-state">No skills</div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #ddd' }}>
                            手法
                          </th>
                          <th style={{ textAlign: 'right', padding: '8px 10px', borderBottom: '1px solid #ddd' }}>
                            例子數目
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {skillOverviewRows.map((row) => (
                          <tr key={row.skillId}>
                            <td style={{ padding: '8px 10px', borderBottom: '1px solid #eee' }}>
                              {row.name}
                            </td>
                            <td style={{ textAlign: 'right', padding: '8px 10px', borderBottom: '1px solid #eee' }}>
                              {row.exampleCount}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WritingSkillExample;
